import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sieveAnalysis as api } from '@/api/endpoints';
import type { SieveProject, SieveProjectSummary, SieveProjectStatus } from '@/types';
import { hasPermission } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { sieveStatusPill } from '@/components/sieve/statusPill';
import { SieveProjectFormModal } from '@/components/sieve/SieveProjectFormModal';

export function SieveAnalysisPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Parytet z SieveAnalysisApi — zapis (POST/PUT) stoi pod SIEVE_EDIT, sam podgląd pod SIEVE_VIEW.
  const canEdit = hasPermission(user, 'SIEVE_EDIT');

  const [items, setItems] = useState<SieveProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<SieveProjectStatus | ''>('');

  // Filtr kontekstowy przychodzi z URL (link z karty ujęcia albo z karty otworu), nie ze stanu
  // komponentu — dzięki temu przetrwa odświeżenie strony i da się go wysłać komuś linkiem.
  const [searchParams, setSearchParams] = useSearchParams();
  const num = (v: string | null) => (v && /^\d+$/.test(v) ? Number(v) : null);
  const intakeFilter = num(searchParams.get('intakeId'));
  const boreholeFilter = num(searchParams.get('boreholeId'));
  const filterActive = intakeFilter !== null || boreholeFilter !== null;

  /** Zdejmuje filtr kontekstowy, zostawiając w URL resztę parametrów nietkniętą. */
  const clearContextFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('intakeId');
    next.delete('boreholeId');
    setSearchParams(next, { replace: true });
  };

  // Jeden modal na dwa tryby: formOpen bez `editing` = tworzenie, z `editing` = edycja.
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SieveProject | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null);

  // Znacznik aktualności: przy szybkim przełączaniu filtrów odpowiedzi potrafią wrócić nie po
  // kolei i starsza nadpisałaby nowszą listę, a jej `finally` zgasiłoby spinner mimo trwającego
  // żądania. Ten sam wzorzec, co przy pobieraniu otworów w formularzu projektu.
  const [odswiez, setOdswiez] = useState(0);
  const reload = () => setOdswiez(n => n + 1);

  useEffect(() => {
    let aktualne = true;
    setLoading(true);
    setErr(null);   // inaczej baner błędu zostaje na ekranie po udanym przeładowaniu
    api.list(statusFilter || undefined, intakeFilter, boreholeFilter)
      .then(l => { if (aktualne) setItems(l); })
      .catch(e => { if (aktualne) setErr(e?.message ?? String(e)); })
      .finally(() => { if (aktualne) setLoading(false); });
    return () => { aktualne = false; };
  }, [statusFilter, intakeFilter, boreholeFilter, odswiez]);

  /** Nazwę otworu/ujęcia bierzemy z pierwszego wiersza wyniku — bez dodatkowego żądania. */
  const filterLabel = (() => {
    const first = items[0];
    if (boreholeFilter !== null) {
      const otwor = first?.well || `#${boreholeFilter}`;
      const ujecie = first?.intakeName ? ` (ujęcie ${first.intakeName})` : '';
      return `Analizy sitowe otworu ${otwor}${ujecie}`;
    }
    return `Analizy sitowe ujęcia ${items[0]?.intakeName || `#${intakeFilter}`}`;
  })();

  const openNew = () => { setEditing(null); setFormOpen(true); };

  /** Lista zwraca tylko podsumowanie, a PUT wymaga kompletu pól — dlatego przed edycją dociągamy projekt. */
  const openEdit = async (id: number) => {
    setEditLoadingId(id); setErr(null);
    try {
      const full = await api.get(id);
      setEditing(full);
      setFormOpen(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setEditLoadingId(null);
    }
  };

  const closeForm = () => { setFormOpen(false); setEditing(null); };

  const onSaved = (saved: SieveProject) => {
    const bylaEdycja = editing !== null;
    closeForm();
    // Nowy projekt otwieramy od razu do wprowadzania próbek; po edycji zostajemy na liście.
    if (bylaEdycja) reload();
    else navigate('/analiza-sitowa/' + saved.id);
  };

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Analiza sitowa / Obsypka</h1>
          <div className="text-ink-400 text-[13px]">
            Dobór obsypki i szczeliny filtra studni z analizy granulometrycznej — porównawczo wg PN i DVGW
          </div>
        </div>
        {canEdit && (
          <button onClick={openNew} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5" /> Nowy projekt
          </button>
        )}
      </header>

      <div className="flex items-center gap-1 mb-4 flex-wrap">
        <button onClick={() => setStatusFilter('')}
          className={'btn btn-sm ' + (statusFilter === '' ? 'btn-primary' : 'btn-ghost')}>Wszystkie</button>
        {(['ACTIVE', 'DONE', 'DRAFT'] as SieveProjectStatus[]).map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={'btn btn-sm ' + (statusFilter === s ? 'btn-primary' : 'btn-ghost')}>
            {s === 'ACTIVE' ? 'W analizie' : s === 'DONE' ? 'Zakończone' : 'Szkice'}
          </button>
        ))}
      </div>

      {filterActive && (
        <div className="card mb-3">
          <div className="card-body !py-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-[13px] text-ink-700">
              <Icon name="filter" className="w-3.5 h-3.5 text-ink-400" />
              <span className="font-semibold">{filterLabel}</span>
              <span className="text-ink-400">— lista zawężona do powiązanych projektów</span>
            </div>
            <button type="button" onClick={clearContextFilter} className="btn btn-ghost btn-sm">
              <Icon name="x" className="w-3.5 h-3.5" /> Pokaż wszystkie
            </button>
          </div>
        </div>
      )}

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}
      {loading && <div className="text-ink-400">Ładowanie…</div>}

      {!loading && items.length === 0 && (
        <div className="card"><div className="card-body text-center text-ink-400 py-8">
          {filterActive
            ? 'Brak analiz sitowych powiązanych z wybranym obiektem.'
            : 'Brak projektów. Kliknij „Nowy projekt", aby rozpocząć analizę sitową.'}
        </div></div>
      )}

      {!loading && items.length > 0 && (
        <div className="card">
          <div className="card-body !p-0">
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Nazwa opracowania</th>
                  <th>Ujęcie / lokalizacja</th>
                  <th className="w-24">Otwór</th>
                  <th className="w-20 text-right">Próbki</th>
                  <th className="w-28">Data</th>
                  <th className="w-28">Status</th>
                  {canEdit && <th className="w-12"></th>}
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.id} onClick={() => navigate('/analiza-sitowa/' + p.id)}>
                    <td>
                      <div className="font-semibold text-ink-900">{p.name}</div>
                      {p.docNo && <div className="text-[11px] text-ink-400">{p.docNo}</div>}
                    </td>
                    <td className="text-ink-600">{p.intakeName || '—'}</td>
                    <td className="text-ink-600">{p.well || '—'}</td>
                    <td className="text-right font-mono tabular-nums">{p.sampleCount}</td>
                    <td className="text-ink-500 font-mono text-[12px]">{p.studyDate || '—'}</td>
                    <td><span className={'pill ' + sieveStatusPill(p.status)}>{p.statusName}</span></td>
                    {canEdit && (
                      <td>
                        {/* stopPropagation — edycja nie może otwierać karty projektu spod spodu. */}
                        <button type="button" title="Edytuj projekt (status, dane, DVGW)"
                          disabled={editLoadingId === p.id}
                          onClick={e => { e.stopPropagation(); openEdit(p.id); }}
                          className="btn btn-ghost btn-sm">
                          <Icon name="edit" className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {formOpen && canEdit && (
        <SieveProjectFormModal project={editing} onClose={closeForm} onSaved={onSaved} />
      )}
    </div>
  );
}
